<template>
  <div class="a2p-main">
    <div class="a2p-eyebrow">{{ eyebrow }}</div>
    <h1 class="a2p-title">Brand &amp; messaging</h1>
    <p class="a2p-sub">
      Here's exactly what we'll file with the carriers on your behalf. Everything is
      pre-filled with wording Twilio has already approved — you can customize any of it,
      but most practices won't need to.
    </p>

    <A2PCallout type="info">
      We register every Teamio practice for <strong>patient care &amp; appointment-reminder</strong>
      messaging — the only kind Teamio sends. There's nothing to choose here.
    </A2PCallout>

    <!-- One shared warning, shown as soon as any card is customized. -->
    <A2PCallout v-if="anyCustomized" type="warn">
      <strong>Editing increases rejection risk.</strong> The default wording above is
      the exact text Twilio's carriers have approved. If you change it, keep it accurate
      to your practice and make sure any sample message still includes
      <strong>"Reply STOP"</strong>.
    </A2PCallout>

    <form @submit.prevent="submit" novalidate>
      <!-- 1 · Brand description -->
      <A2PField
        :num="1"
        label="Brand description"
        help="A short paragraph the carriers read during review. Accurate and specific to your practice."
        :value="values.brandDescription"
      >
        <template v-if="!customized.brandDescription">
          <div class="a2p-preview">
            <div class="a2p-preview-caption">Here's what we'll file for you</div>
            <p class="a2p-preview-text">{{ values.brandDescription }}</p>
            <button type="button" class="a2p-customize-link" @click="customize('brandDescription')">
              Customize
            </button>
          </div>
        </template>
        <template v-else>
          <textarea
            v-model="values.brandDescription"
            class="a2p-field-textarea"
            rows="4"
            required
          />
          <div class="a2p-reset-row">
            <button type="button" class="a2p-reset-link" @click="reset('brandDescription')">
              Reset to recommended
            </button>
          </div>
        </template>
      </A2PField>

      <!-- 2 · Sample message -->
      <A2PField
        :num="2"
        label="Sample message"
        help="An actual example of the most common message a patient would receive. Always includes <strong>Reply STOP</strong>."
        :value="values.sampleMessage"
      >
        <template v-if="!customized.sampleMessage">
          <div class="a2p-preview">
            <div class="a2p-preview-caption">Here's what we'll file for you</div>
            <p class="a2p-preview-text">{{ values.sampleMessage }}</p>
            <button type="button" class="a2p-customize-link" @click="customize('sampleMessage')">
              Customize
            </button>
          </div>
        </template>
        <template v-else>
          <textarea
            v-model="values.sampleMessage"
            class="a2p-field-textarea"
            rows="2"
            required
          />
          <div class="a2p-reset-row">
            <button type="button" class="a2p-reset-link" @click="reset('sampleMessage')">
              Reset to recommended
            </button>
          </div>
        </template>
      </A2PField>

      <!-- 3 · Opt-in / opt-out process -->
      <A2PField
        :num="3"
        label="Opt-in / opt-out process"
        help="How patients consent, and how they can stop. This is the load-bearing compliance text — the carriers scan it closely."
        :value="values.optInOut"
      >
        <template v-if="!customized.optInOut">
          <div class="a2p-preview">
            <div class="a2p-preview-caption">Here's what we'll file for you</div>
            <div class="a2p-preview-block">
              <div class="a2p-preview-block-label">How patients opt in</div>
              <p class="a2p-preview-text">{{ gen.optIn }}</p>
            </div>
            <div class="a2p-preview-block">
              <div class="a2p-preview-block-label">How patients opt out</div>
              <p class="a2p-preview-text">{{ gen.optOut }}</p>
            </div>
            <button type="button" class="a2p-customize-link" @click="customize('optInOut')">
              Customize
            </button>
          </div>
        </template>
        <template v-else>
          <textarea
            v-model="values.optInOut"
            class="a2p-field-textarea"
            rows="6"
            required
          />
          <div class="a2p-reset-row">
            <button type="button" class="a2p-reset-link" @click="reset('optInOut')">
              Reset to recommended
            </button>
          </div>
        </template>
      </A2PField>

      <div class="a2p-actions">
        <button type="button" class="a2p-btn a2p-btn-secondary" @click="$emit('back')">
          Back
        </button>
        <button type="submit" class="a2p-btn a2p-btn-primary" :disabled="!isFormValid">
          Continue
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import A2PField from './components/A2PField.vue';
import A2PCallout from './components/A2PCallout.vue';

/**
 * Pull "City, ST" out of a free-form single-line business address.
 *
 * Addresses reach us as one comma-joined string ("3088 Basswood Blvd STE 150,
 * Fort Worth, TX 76137"), so the city is the second-to-last comma segment and
 * the state is the leading token of the last one. Returns '' when the shape
 * doesn't match — the caller then omits the location clause rather than
 * emitting a malformed one.
 */
export function extractCityState(address) {
  if (!address || typeof address !== 'string') return '';
  const parts = address.split(',').map(p => p.trim()).filter(Boolean);
  if (parts.length < 2) return '';
  const last = parts[parts.length - 1];
  // Last segment is normally "TX 76137" (or "TX"); take the 2-letter state.
  const state = (last.match(/\b([A-Za-z]{2})\b/) || [])[1];
  const city = parts[parts.length - 2];
  if (!state || !city || /\d/.test(city)) return '';
  return `${city}, ${state.toUpperCase()}`;
}

/**
 * Last name from a representative's full name, for the "Dr. {LastName}" sample.
 * Trailing credentials (DDS, DMD, PLLC…) are stripped so "Calvin Eastwood DDS"
 * yields "Eastwood", matching the approved samples (Dr. Unnadkat, Dr. Figueredo,
 * Dr. Cherukuri, Dr. Hurcomb, Dr. Clarke).
 */
export function lastNameOf(fullName) {
  if (!fullName || typeof fullName !== 'string') return '';
  const CREDENTIALS = /^(dds|dmd|md|do|phd|rdh|rda|esq|cpa|jd|jr|sr|ii|iii|iv|pllc|llc|pc|pa|inc)$/i;
  const tokens = fullName
    .replace(/[.,]/g, ' ')
    .split(/\s+/)
    // Single characters are dropped as initials — same rule as
    // a2p-service/legal-name-check.js::extractRequiredTokens. Without it a
    // dotted credential disintegrates and the LAST initial wins:
    // "Dr. Jane Smith, D.D.S." → tokens [Dr, Jane, Smith, D, D, S] → "S".
    .filter(t => t && t.length > 1 && !CREDENTIALS.test(t));
  if (!tokens.length) return '';
  return tokens[tokens.length - 1];
}

export default {
  name: 'A2PWizardBrandUseCase',
  components: { A2PField, A2PCallout },
  props: {
    eyebrow: String,
    brandDescription: String,
    sampleMessage: String,
    optInOut: String,
    messageFrequency: String,
    officePhone: String,
    // All four are scoped to THIS registration (passed by the wizard) so the
    // generated TCR copy can never be built from the operator's own site.
    practiceName: String,
    businessName: String,
    businessAddress: String,
    repName: String,
  },
  data() {
    // `practiceName` is passed down from the wizard and is already scoped to THIS
    // registration's site. Reading `$store.state.customer.siteName` here was the
    // same cross-tenant bug as the Office Phone prefill: while a super admin
    // files for another practice, that store value is the OPERATOR's site, so
    // the operator's practice name would be written into the customer's TCR
    // brand description. It only read "Our dental practice" on 2026-08-26
    // because the store value happened to be empty at that moment.
    const siteName = (this.practiceName && this.practiceName.trim()) || '';
    // Office phone + message frequency come from earlier wizard steps.
    // Fall back to clearly broken placeholders so the user can't miss them
    // (the prior '(555) 123-4567' placeholder triggered automatic Twilio
    // rejection when accidentally left in — see a2p-service README #17).
    const phone = (this.officePhone && this.officePhone.trim()) || '[YOUR OFFICE PHONE]';
    const freq = (this.messageFrequency && this.messageFrequency.trim()) || 'up to 4 messages per month';
    // genOptIn + genOptOut are concatenated with a space in submit() and sent as
    // `optInOut` (stored as OptInOutProcedures, which OVERRIDES the backend's
    // default). The split point is at "Opt out any time…" so the concatenation
    // reproduces, verbatim, the body Twilio APPROVED for Brester Dentistry on
    // 2026-09-28 — profile v2, the standard for new registrations since that day
    // (Saumil). v1 described REQUIRED consent boxes + phone, which Twilio rejected
    // as 30923 "Forced Consent". DO NOT REWRITE these strings — only wording
    // Twilio has approved goes here (a2p-service README "2026-09-28" + #17,
    // A2P-CAMPAIGN-HISTORY.md "Opt-in rule profiles").
    const genOptIn = 'Patients opt in to SMS appointment and care reminders via our online SMS Consent Form. Participation is entirely voluntary: NO field is required. The form can be submitted without a mobile number, without checking the SMS consent box, and without a signature. Declining does not affect care or scheduling, as the form states. The SMS consent checkbox is separate and unchecked by default. The form states message frequency varies and that message and data rates may apply.';
    const genOptOut = `Opt out any time by replying STOP, calling ${phone}, or a written request; reply HELP for help. Opt-outs are processed immediately.`;
    // RETIRED v1 default (Smile Bar / Tanya Rajdev), kept verbatim so a registration
    // SAVED with the untouched v1 default is upgraded to v2 on resume instead of
    // being treated as a customization — and as the revert target.
    const legacyV1OptInOut = `Patients opt in to receive SMS appointment and care reminders by completing our online SMS Consent Form. The form requires patients to check four explicit consent checkboxes, provide their name, signature, and mobile phone number. The form clearly states: message frequency ${freq}, message and data rates may apply, reply STOP to opt out at any time, reply HELP for assistance. Patients can opt out at any time by: (1) replying STOP to any text message, (2) contacting our office at ${phone}, or (3) submitting a written request. Opt-out requests are processed immediately.`;
    // Brand description + sample message follow the canonical template in
    // `a2p-registration/SKILL.md` ("brandDescription" template, ~line 479),
    // which is what the five most recent registrations used — Schuyler, Family
    // EcoDental, Harrisburg, Kids Dental and Tulip Tree — all of which cleared
    // review. The previous generic wording ("Our dental practice is a
    // full-service dental practice…") is ALSO proven-approved (Allay and Smile
    // Bar both hold it), so this is an alignment/quality change, NOT a
    // rejection fix: per A2P-CAMPAIGN-HISTORY.md the copy text has never been
    // the differentiator in any of our rejections. Two concrete reasons to
    // prefer the template anyway:
    //   1. It names the entity + city, which is the axis Twilio 30891
    //      ("unverifiable brand/website") actually turns on.
    //   2. It drops "practice updates" — marketing-adjacent language that our
    //      own SKILL.md:475 and a2p-service README:635 tell us to avoid on
    //      CUSTOMER_CARE campaigns, so leaving it in contradicted our own rule.
    // NOTE: "practice updates" is deliberately NOT added to the backend's
    // MARKETING_KEYWORDS hard-fail list — Allay and Smile Bar were both APPROVED
    // carrying it, so a hard-fail would block content Twilio demonstrably accepts.
    const city = extractCityState(this.businessAddress);
    const legalName = (this.businessName && this.businessName.trim()) || siteName;
    const location = city ? ` in ${city}` : '';
    const dba = (siteName && legalName && siteName !== legalName) ? `, doing business as ${siteName},` : '';
    const genBrandDescription = `${legalName}${dba} is a dental practice${location} providing dental care services. We send SMS messages to patients for appointment reminders, treatment follow-ups, and care updates.`;
    // Matches the approved sample shape: "…with Dr. {LastName} at {Practice}."
    // Falls back to a loud placeholder rather than the old hardcoded
    // "Dr. Smith", which reached two live prod registrations (Allay, Smile Bar).
    const doctor = lastNameOf(this.repName) || '[DOCTOR LAST NAME]';
    const practice = siteName || '[PRACTICE NAME]';
    // "HELP for help" matches all three samples on Brester's approved campaign
    // (Twilio support, 2026-09-25: a sample must carry STOP and HELP).
    const genSampleMessage = `Reminder: You have a dental appointment tomorrow at 2:00 PM with Dr. ${doctor} at ${practice}. Reply STOP to unsubscribe, HELP for help.`;
    const genOptInOut = `${genOptIn} ${genOptOut}`;

    // Fall back to any saved value from props (round-trip: user customized, went
    // forward, came back). A saved value that differs from the generated default
    // means it was customized before → open that card in edit mode. A saved value
    // equal to the untouched v1 default is NOT a customization → upgrade to v2.
    const savedBrand = (this.brandDescription && this.brandDescription.trim()) ? this.brandDescription : genBrandDescription;
    const savedSample = (this.sampleMessage && this.sampleMessage.trim()) ? this.sampleMessage : genSampleMessage;
    const hasSavedOptInOut = !!(this.optInOut && this.optInOut.trim()) && this.optInOut !== legacyV1OptInOut;
    const savedOptInOut = hasSavedOptInOut ? this.optInOut : genOptInOut;

    return {
      gen: {
        brandDescription: genBrandDescription,
        sampleMessage: genSampleMessage,
        optIn: genOptIn,
        optOut: genOptOut,
        optInOut: genOptInOut,
      },
      values: {
        brandDescription: savedBrand,
        sampleMessage: savedSample,
        optInOut: savedOptInOut,
      },
      customized: {
        brandDescription: savedBrand !== genBrandDescription,
        sampleMessage: savedSample !== genSampleMessage,
        optInOut: savedOptInOut !== genOptInOut,
      },
    };
  },
  computed: {
    anyCustomized() {
      return this.customized.brandDescription || this.customized.sampleMessage || this.customized.optInOut;
    },
    isFormValid() {
      return this.values.brandDescription.trim().length > 0 &&
        this.values.sampleMessage.trim().length > 0 &&
        this.values.optInOut.trim().length > 0;
    },
  },
  methods: {
    customize(field) {
      // Reveal the textarea, pre-filled with the current (generated) wording.
      this.customized[field] = true;
    },
    reset(field) {
      this.values[field] = this.gen[field];
      this.customized[field] = false;
    },
    submit() {
      if (!this.isFormValid) return;
      // Emit EXACTLY the three keys the wizard form + submit payload consume.
      // No useCase / sampleMessage2 / optInProcess leakage (backend derives the
      // use case from the description + sample text — a2p-service index.js
      // determineA2PCampaignUseCase — so a submitted useCase is ignored).
      this.$emit('next', {
        brandDescription: this.values.brandDescription,
        sampleMessage: this.values.sampleMessage,
        optInOut: this.values.optInOut,
      });
    },
  },
};
</script>

<style src="./a2p-wizard.css"></style>

<style scoped>
.a2p-preview {
  background: var(--a2p-surface-2);
  border: 1px solid var(--a2p-border);
  border-radius: var(--a2p-radius-s);
  padding: 14px 16px;
}
.a2p-preview-caption {
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--a2p-fg-3);
  font-weight: 600;
  margin-bottom: 8px;
}
.a2p-preview-text {
  margin: 0;
  font-size: 14px;
  color: var(--a2p-fg-2);
  line-height: 1.6;
  white-space: pre-wrap;
}
.a2p-preview-block + .a2p-preview-block {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid var(--a2p-border);
}
.a2p-preview-block-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--a2p-fg);
  margin-bottom: 4px;
}
.a2p-customize-link {
  margin-top: 12px;
  background: transparent;
  border: none;
  padding: 0;
  color: var(--a2p-accent);
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}
.a2p-customize-link:hover {
  text-decoration: underline;
}
.a2p-reset-row {
  margin-top: 8px;
  text-align: right;
}
.a2p-reset-link {
  background: transparent;
  border: none;
  padding: 0;
  color: var(--a2p-fg-3);
  font-family: inherit;
  font-size: 12px;
  cursor: pointer;
}
.a2p-reset-link:hover {
  color: var(--a2p-accent);
  text-decoration: underline;
}
</style>
